// ClientView — workspace page for the selected client + week. Selecting a
// signal drives the detail panel, impact matrix, and action backlog. Optional
// `?track=` deep link.

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import useClientView from "../../../hooks/useClientView";
import useMarketTracks from "../../../hooks/useMarketTracks";
import { relativeFromIso, dedupeTrackCards, trackCardKey } from "../../../lib/clients";
import { useAuth } from "../../../auth/AuthContext";
import ClientExecutiveLens from "./ClientExecutiveLens";
import ClientTrackBoard from "./ClientTrackBoard";
import ClientTrackDetailPanel from "./ClientTrackDetailPanel";
import ScopedImpactMatrix from "./ScopedImpactMatrix";
import { trackEvent } from "../../../lib/analytics";
import ScopedActionBacklog from "./ScopedActionBacklog";
import ClientHistoryPanel from "./ClientHistoryPanel";

const ClientPicker = ({ clients, value, onChange }) => (
    <label className="flex flex-col gap-1" data-testid="client-picker">
        <span className="eyebrow">Client</span>
        <select
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            className="form-select num text-sm min-w-[160px]"
        >
            {clients.map((c) => (
                <option key={c.id} value={c.id} disabled={c.unavailable}>
                    {c.name}{c.unavailable ? " (unavailable)" : ""}
                </option>
            ))}
        </select>
    </label>
);

const WeekPicker = ({ availableYearWeeks, value, onChange }) => {
    if (!availableYearWeeks?.length) return null;
    return (
        <label className="flex flex-col gap-1" data-testid="week-picker">
            <span className="eyebrow">Week</span>
            <select
                value={value || ""}
                onChange={(e) => onChange(e.target.value)}
                className="form-select num text-sm min-w-[140px]"
            >
                {availableYearWeeks.map((w) => (
                    <option key={w} value={w}>{w}</option>
                ))}
            </select>
        </label>
    );
};

const FreshnessRow = ({ schemaVersion, generatedAt, latestYearWeek, selectedYearWeek, quality }) => {
    const rel = relativeFromIso(generatedAt);
    return (
        <div className="text-[11px] text-[hsl(var(--mute))] num flex flex-wrap gap-x-3 gap-y-0.5" data-testid="client-freshness">
            {schemaVersion && <span>Schema {schemaVersion}</span>}
            {latestYearWeek && <span>Latest week {latestYearWeek}</span>}
            {selectedYearWeek && selectedYearWeek !== latestYearWeek && <span className="text-[hsl(var(--warning))]">Viewing {selectedYearWeek}</span>}
            {generatedAt && <span>Generated {rel}</span>}
            {quality?.approvedOnly && <span>Approved-only feed</span>}
        </div>
    );
};

const ClientView = () => {
    const { clientId, yearWeek } = useParams();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const trackParam = searchParams.get("track");
    const [selectedTrackId, setSelectedTrackId] = useState(trackParam || null);
    const { isAdmin } = useAuth();

    const data = useClientView({ clientId, yearWeek });
    // Lightweight join: fetch the governed market-track registry so the
    // Client board can nest supporting patterns and weekly movements
    // under their parent signal. Read-only, structural metadata only.
    const market = useMarketTracks({ showExploratory: true });
    const marketTracksByTrackId = useMemo(() => {
        const m = new Map();
        for (const t of market.tracks || []) {
            if (t?.TrackID) m.set(t.TrackID, t);
        }
        return m;
    }, [market.tracks]);

    // Stable (clientId, selectedWeek) tag used by dedupe + React keys + diagnostics.
    const selectedWeek = data.selectedYearWeek || yearWeek || data.latestYearWeek || "";
    const viewKey = `${clientId || ""}|${selectedWeek}`;

    // Hard frontend dedupe: even if the upstream JSON briefly contains
    // duplicate trackCards (n8n workflow recompute window), we never render
    // the same (clientId, week, canonicalTrackId/trackId) twice.
    const trackCards = useMemo(
        () => dedupeTrackCards(data.trackCards, { clientId, selectedWeek }),
        [data.trackCards, clientId, selectedWeek]
    );

    // Dev diagnostics — auto-disabled in production builds. Toggle on/off in
    // the browser console with `window.__LODESTAR_DEBUG__ = true|false`.
    useEffect(() => {
        const enabled = (typeof window !== "undefined" && window.__LODESTAR_DEBUG__ === true)
            || (typeof process !== "undefined" && process.env && process.env.NODE_ENV !== "production");
        if (!enabled) return;
        const ids = trackCards.map((c) => c?.trackId);
        // eslint-disable-next-line no-console
        console.debug("[ClientView]", {
            clientId,
            selectedWeek,
            latestYearWeek: data.latestYearWeek,
            availableYearWeeks: data.availableYearWeeks,
            generatedAt: data.generatedAt,
            apiUrl: yearWeek
                ? `/api/clients/${clientId}/${yearWeek}`
                : `/api/clients/${clientId}`,
            rawTrackCardsCount: (data.trackCards || []).length,
            dedupedTrackCardsCount: trackCards.length,
            historyTrackCardsCount: (data.history?.trackCardsByWeek?.[selectedWeek] || []).length,
            renderedTrackIds: ids,
            renderedScores: trackCards.map((c) => ({
                trackId: c?.trackId,
                relevance: c?.client?.relevanceScore,
                market: c?.market?.score,
            })),
        });
    }, [clientId, selectedWeek, yearWeek, trackCards, data.trackCards, data.history, data.latestYearWeek, data.availableYearWeeks, data.generatedAt]);

    // -----------------------------------------------------------------------
    // Selected signal — reset on EVERY (clientId, selectedWeek) change. We
    // never carry a selectedTrackId across client/week boundaries, even if
    // the same trackId happens to exist in both. The deep-link ?track= param
    // is honoured on first mount only and stripped on any later re-selection.
    // -----------------------------------------------------------------------
    const lastViewKeyRef = useRef(viewKey);
    useEffect(() => {
        if (lastViewKeyRef.current === viewKey) return;
        lastViewKeyRef.current = viewKey;
        setSelectedTrackId(null); // explicit reset; auto-select effect picks first
    }, [viewKey]);

    useEffect(() => {
        if (clientId) return;
        if (!data.defaultClientId) return;
        navigate(`/client/${data.defaultClientId}`, { replace: true });
    }, [clientId, data.defaultClientId, navigate]);

    // Sync URL → state when the ?track= param changes externally
    useEffect(() => {
        if (trackParam && trackParam !== selectedTrackId) {
            setSelectedTrackId(trackParam);
        }
    }, [trackParam]); // eslint-disable-line react-hooks/exhaustive-deps

    // Keep selected track in sync with the deduped card list. Auto-select the
    // first card once cards are loaded. Do NOT clear the deep-linked id while
    // the card list is still loading.
    useEffect(() => {
        if (!trackCards.length) return;
        if (selectedTrackId && trackCards.find((c) => c.trackId === selectedTrackId)) return;
        setSelectedTrackId(trackCards[0].trackId);
    }, [trackCards, selectedTrackId]);

    // Sync state → URL: write ?track= when it differs.
    useEffect(() => {
        if (!selectedTrackId) return;
        if (searchParams.get("track") === selectedTrackId) return;
        const next = new URLSearchParams(searchParams);
        next.set("track", selectedTrackId);
        setSearchParams(next, { replace: true });
    }, [selectedTrackId, searchParams, setSearchParams]);

    const trackCardsById = useMemo(() => {
        const m = {};
        for (const c of trackCards) if (c?.trackId) m[c.trackId] = c;
        return m;
    }, [trackCards]);

    const selectedCard = useMemo(
        () => (selectedTrackId ? trackCardsById[selectedTrackId] || null : null),
        [trackCardsById, selectedTrackId]
    );

    const onPickClient = (next) => {
        if (next === clientId) return;
        // Reset ?track= when switching clients — id is client-specific.
        navigate(`/client/${next}`);
    };
    const onPickWeek = (next) => {
        if (!clientId || !next) return;
        const qs = selectedTrackId ? `?track=${encodeURIComponent(selectedTrackId)}` : "";
        navigate(`/client/${clientId}/${next}${qs}`);
    };

    const focusTrack = (tid) => {
        if (!tid) return;
        if (!trackCards.find((c) => c.trackId === tid)) return;
        setSelectedTrackId(tid);
        const board = document.querySelector('[data-testid="client-track-board"]');
        if (board) window.scrollTo({ top: board.offsetTop - 16, behavior: "smooth" });
    };

    // --------------------------------------------------------------- empty states
    if (data.clients && data.clients.length === 0 && !data.loading) {
        return (
            <section className="card-flat p-10 text-center" data-testid="client-view-no-clients">
                <div className="eyebrow">Client view</div>
                <h2 className="display text-2xl mt-2">No clients configured</h2>
                <p className="text-sm text-[hsl(var(--mute))] mt-3 max-w-md mx-auto">
                    Add a client ID to the <span className="num">CLIENT_IDS</span> backend env var and publish a matching JSON file to the data repository to enable the Client View.
                </p>
            </section>
        );
    }

    if (data.error) {
        const status = data.error?.status;
        const isMissing = status === 404 || (data.error?.message && /Unknown/.test(data.error.message));
        return (
            <section className="card-flat p-10 text-center" data-testid="client-view-error">
                <div className="eyebrow">Client view</div>
                <h2 className="display text-2xl mt-2">{isMissing ? "Client data not available" : "Failed to load client view"}</h2>
                <p className="text-sm text-[hsl(var(--mute))] mt-3 max-w-md mx-auto">
                    {typeof data.error?.error === "string" ? data.error.error : (data.error?.message || "The backend could not return a payload for this view.")}
                </p>
                {Array.isArray(data.error?.availableYearWeeks) && data.error.availableYearWeeks.length > 0 && (
                    <div className="mt-4 text-xs text-[hsl(var(--ink-soft))]" data-testid="error-available-weeks">
                        <div className="mb-1">Available weeks:</div>
                        <ul className="flex flex-wrap justify-center gap-2">
                            {data.error.availableYearWeeks.map((w) => (
                                <li key={w}>
                                    <button type="button" onClick={() => onPickWeek(w)} className="num text-[hsl(var(--accent-ink))] hover:underline" data-testid={`available-week-${w}`}>{w}</button>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </section>
        );
    }

    // --------------------------------------------------------------- render
    return (
        <div className="space-y-6" data-testid="client-view">
            <section className="card-flat p-6">
                <div className="flex flex-wrap items-end gap-6">
                    <div>
                        <div className="eyebrow">Client view</div>
                        <h1 className="display text-3xl mt-1 leading-snug">{data.client?.clientName || clientId || "—"}</h1>
                        <FreshnessRow
                            schemaVersion={data.schemaVersion}
                            generatedAt={data.generatedAt}
                            latestYearWeek={data.latestYearWeek}
                            selectedYearWeek={data.selectedYearWeek || yearWeek}
                            quality={data.quality}
                        />
                    </div>
                    {isAdmin && data.clients && data.clients.length > 1 && (
                        <ClientPicker clients={data.clients} value={clientId} onChange={onPickClient} />
                    )}
                    <WeekPicker
                        availableYearWeeks={data.availableYearWeeks}
                        value={yearWeek || data.latestYearWeek}
                        onChange={onPickWeek}
                    />
                </div>
            </section>

            {data.loading && !data.weeklyLens && (
                <div className="card-flat p-10 text-center text-sm text-[hsl(var(--mute))]" data-testid="client-loading">Loading client view…</div>
            )}

            {data.weeklyLens && (
                <ClientExecutiveLens
                    weeklyLens={data.weeklyLens}
                    onSelectTrack={focusTrack}
                    trackCardsById={trackCardsById}
                />
            )}

            <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">
                <div className="xl:col-span-3 space-y-6">
                    <ClientTrackBoard
                        trackCards={trackCards}
                        selectedTrackId={selectedTrackId}
                        onSelect={(tid) => {
                            setSelectedTrackId(tid);
                            if (tid) trackEvent("client_track_inspect", { track_id: tid, client_id: clientId || "" });
                        }}
                        clientId={clientId}
                        selectedWeek={selectedWeek}
                        marketTracksByTrackId={marketTracksByTrackId}
                    />
                    <ScopedImpactMatrix
                        selectedTrack={selectedCard}
                        exposureMatrix={data.organizationalExposureMatrix}
                        impactCellAssessments={data.impactCellAssessments}
                        clientProfile={data.clientProfile}
                        onSelectTrack={focusTrack}
                    />
                </div>
                <div className="xl:col-span-2">
                    <div className="xl:sticky xl:top-4 space-y-6">
                        <ClientTrackDetailPanel
                            card={selectedCard}
                            onSelectTrack={focusTrack}
                            validTrackIds={new Set(trackCards.flatMap((c) => [c?.trackId, c?.canonicalTrackId].filter(Boolean)))}
                            trackLabelById={(() => {
                                // Build a `{trackId|canonicalTrackId -> displayName}` lookup so
                                // BlindSpot linked-signal chips render the same human-readable
                                // names as the Client Signal Board. Falls back through:
                                //   stableTrackName → signalName → latestWeeklyFraming → raw id.
                                const m = {};
                                for (const c of trackCards) {
                                    if (!c) continue;
                                    const label = c.stableTrackName
                                        || c.signalName
                                        || c.latestWeeklyFraming
                                        || c.trackId
                                        || c.canonicalTrackId
                                        || null;
                                    if (!label) continue;
                                    if (c.trackId) m[c.trackId] = label;
                                    if (c.canonicalTrackId) m[c.canonicalTrackId] = label;
                                }
                                return m;
                            })()}
                        />
                    </div>
                </div>
            </div>

            <ScopedActionBacklog
                selectedTrack={selectedCard}
                actionBacklog={data.actionBacklog}
                onSelectTrack={focusTrack}
                trackCardsById={trackCardsById}
            />

            <ClientHistoryPanel
                clientId={clientId}
                availableYearWeeks={data.availableYearWeeks}
                selectedYearWeek={data.selectedYearWeek || yearWeek || data.latestYearWeek}
                history={data.history}
            />
        </div>
    );
};

export default ClientView;
